import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import api from '@/api/client'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog'
import { Badge } from '@/components/ui/badge'
import { Plus } from 'lucide-react'
import type { PaginatedResponse, RoleResponse, ProductResponse } from '@/api/types'
import { useNavigate } from 'react-router-dom'
import { HelpBanner } from '@/components/ui/help-banner'

export function RolesPage() {
  const { t } = useTranslation(['roles', 'common'])
  const navigate = useNavigate()
  const [offset, setOffset] = useState(0)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [newName, setNewName] = useState('')
  const [newScope, setNewScope] = useState('TENANT')
  const [newProductId, setNewProductId] = useState('')
  const queryClient = useQueryClient()
  const limit = 20

  const { data, isLoading } = useQuery({
    queryKey: ['roles', offset],
    queryFn: () => api.get<PaginatedResponse<RoleResponse>>(`/roles?offset=${offset}&limit=${limit}`),
  })

  const { data: productsData } = useQuery({
    queryKey: ['products-all'],
    queryFn: () => api.get<PaginatedResponse<ProductResponse>>('/products?offset=0&limit=100'),
  })
  const products = productsData?.data.data ?? []
  const productLookup = Object.fromEntries(products.map((p) => [p.id, p.name]))

  const createMutation = useMutation({
    mutationFn: () => api.post('/roles', { name: newName, scope_level: newScope, product_id: Number(newProductId) }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['roles'] })
      setDialogOpen(false)
      setNewName('')
      setNewProductId('')
    },
  })

  const roles = data?.data.data ?? []
  const total = data?.data.total ?? 0

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-semibold">{t('nav.roles', { ns: 'common' })}</h2>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger render={<Button />}>
            <Plus className="h-4 w-4 mr-2" />{t('newRole')}
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>{t('createRole')}</DialogTitle></DialogHeader>
            <form onSubmit={(e) => { e.preventDefault(); createMutation.mutate() }} className="flex flex-col gap-4">
              <div>
                <Label htmlFor="role-name">{t('labels.name', { ns: 'common' })}</Label>
                <Input id="role-name" value={newName} onChange={(e) => setNewName(e.target.value)} required />
              </div>
              <div>
                <Label htmlFor="role-scope">{t('scopeLevel')}</Label>
                <select id="role-scope" value={newScope} onChange={(e) => setNewScope(e.target.value)} className="w-full border border-input rounded-md px-3 py-2 text-sm bg-background">
                  <option value="PLATFORM">{t('scopes.platform')}</option>
                  <option value="TENANT">{t('scopes.tenant')}</option>
                  <option value="BOOKMAKER">{t('scopes.bookmaker')}</option>
                </select>
              </div>
              <div>
                <Label htmlFor="role-product">{t('labels.product', { ns: 'common' })}</Label>
                <select id="role-product" value={newProductId} onChange={(e) => setNewProductId(e.target.value)} className="w-full border border-input rounded-md px-3 py-2 text-sm bg-background" required>
                  <option value="">{t('placeholders.selectProduct', { ns: 'common' })}</option>
                  {products.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
              <Button type="submit" disabled={createMutation.isPending}>{t('buttons.create', { ns: 'common' })}</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <HelpBanner storageKey="roles">
        <p>{t('help.line1')}</p>
        <p>{t('help.line2')}</p>
        <p>{t('help.line3')}</p>
      </HelpBanner>

      {isLoading ? <p className="text-muted-foreground">{t('loading', { ns: 'common' })}</p> : (
        <>
          <div className="border rounded-md">
            <table className="w-full text-sm">
              <thead><tr className="border-b bg-muted/50">
                <th className="text-left p-3 font-medium">{t('labels.name', { ns: 'common' })}</th>
                <th className="text-left p-3 font-medium">{t('scope', { ns: 'roles' })}</th>
                <th className="text-left p-3 font-medium">{t('labels.product', { ns: 'common' })}</th>
                <th className="text-left p-3 font-medium">{t('permissions', { ns: 'roles' })}</th>
                <th className="text-left p-3 font-medium">{t('labels.created', { ns: 'common' })}</th>
              </tr></thead>
              <tbody>
                {roles.map((r) => (
                  <tr key={r.id} className="border-b last:border-0 hover:bg-muted/30 cursor-pointer" onClick={() => navigate(`/roles/${r.id}`)}>
                    <td className="p-3 font-medium">{r.name}</td>
                    <td className="p-3"><Badge variant="outline">{r.scope_level}</Badge></td>
                    <td className="p-3">{r.product_id ? (productLookup[r.product_id] ?? `#${r.product_id}`) : '—'}</td>
                    <td className="p-3 text-muted-foreground">{r.permissions?.length ?? 0}</td>
                    <td className="p-3 text-muted-foreground">{new Date(r.created_at).toLocaleDateString()}</td>
                  </tr>
                ))}
                {roles.length === 0 && <tr><td colSpan={5} className="p-3 text-center text-muted-foreground">{t('empty')}</td></tr>}
              </tbody>
            </table>
          </div>
          <div className="flex items-center justify-between mt-4">
            <span className="text-sm text-muted-foreground">{t('labels.total', { ns: 'common' })}: {total}</span>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - limit))}>{t('pagination.previous', { ns: 'common' })}</Button>
              <Button variant="outline" size="sm" disabled={offset + limit >= total} onClick={() => setOffset(offset + limit)}>{t('pagination.next', { ns: 'common' })}</Button>
            </div>
          </div>
        </>
      )}
    </div>
  )
}
